import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Eye,
  Plus,
  Trash2,
  Building2,
  Tag,
  Search,
  Users2,
  MapPin,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { SmartAlertItem } from "@/components/intelligence/SmartAlertItem";
import { EmptyState } from "@/components/intelligence/EmptyState";
import { AddCriterioDialog } from "@/components/watchlist/AddCriterioDialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { watchlistService } from "@/services/watchlist.service";
import type { WatchlistItem, WatchlistKind } from "@/types";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/watchlist")({
  head: () => ({
    meta: [
      { title: "Watchlist y alertas IA — LicitaPro" },
      {
        name: "description",
        content:
          "Sigue organismos, categorías, competidores y keywords. Recibe alertas inteligentes.",
      },
      { property: "og:title", content: "Watchlist — LicitaPro" },
      {
        property: "og:description",
        content: "Vigilancia activa de oportunidades públicas con copiloto IA.",
      },
    ],
  }),
  component: WatchlistPage,
});

const kindMeta: Record<
  WatchlistKind,
  { icon: React.ComponentType<{ className?: string }>; label: string }
> = {
  organismo: { icon: Building2, label: "Organismo" },
  categoria: { icon: Tag, label: "Categoría" },
  keyword: { icon: Search, label: "Keyword" },
  competidor: { icon: Users2, label: "Competidor" },
  region: { icon: MapPin, label: "Región" },
};

function WatchlistPage() {
  const queryClient = useQueryClient();
  const [porEliminar, setPorEliminar] = useState<WatchlistItem | null>(null);

  const criterios = useQuery({ queryKey: ["watchlist"], queryFn: watchlistService.list });
  const alertas = useQuery({
    queryKey: ["watchlist", "alerts"],
    queryFn: watchlistService.alerts,
  });

  const remove = useMutation({
    mutationFn: (id: string) => watchlistService.remove(id),
    onSuccess: () => {
      toast.success("Criterio eliminado");
      queryClient.invalidateQueries({ queryKey: ["watchlist"] });
      setPorEliminar(null);
    },
    onError: () => toast.error("No pudimos eliminar el criterio"),
  });

  const setActivo = useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) =>
      watchlistService.setActivo(id, activo),
    onSuccess: (_data, { activo }) => {
      toast.success(activo ? "Criterio reactivado" : "Criterio en pausa");
      queryClient.invalidateQueries({ queryKey: ["watchlist"] });
    },
    onError: () => toast.error("No pudimos actualizar el criterio"),
  });

  const items = criterios.data ?? [];

  return (
    <AppShell
      title="Watchlist"
      subtitle="Vigilancia activa · El copiloto te avisa cuando algo importante aparece"
      actions={
        <AddCriterioDialog
          trigger={
            <button className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-primary text-primary-foreground hover:opacity-90 transition">
              <Plus className="size-3.5" /> Agregar criterio
            </button>
          }
        />
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card shadow-elegant overflow-hidden">
          <div className="p-5 border-b border-border flex items-center justify-between">
            <div>
              <h3 className="font-display font-semibold flex items-center gap-2">
                <Eye className="size-4 text-primary" /> Criterios seguidos
              </h3>
              <p className="text-xs text-muted-foreground">
                {criterios.isLoading
                  ? "Cargando…"
                  : `${items.filter((i) => i.activo).length} activos de ${items.length}`}
              </p>
            </div>
          </div>

          {criterios.isLoading ? (
            <div className="divide-y divide-border">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-3 p-4">
                  <Skeleton className="size-9 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-3.5 w-52" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                  <Skeleton className="h-6 w-10" />
                </div>
              ))}
            </div>
          ) : criterios.isError ? (
            <EmptyState
              icon={AlertTriangle}
              title="No pudimos cargar tu watchlist"
              description="Revisa tu conexión y vuelve a intentarlo."
              action={
                <Button variant="outline" size="sm" onClick={() => criterios.refetch()}>
                  Reintentar
                </Button>
              }
            />
          ) : items.length === 0 ? (
            <EmptyState
              icon={Eye}
              title="Aún no sigues ningún criterio"
              description="Agrega organismos, categorías, keywords, competidores o regiones para que el copiloto vigile el mercado por ti."
              action={
                <AddCriterioDialog
                  trigger={
                    <Button size="sm">
                      <Plus className="size-3.5 mr-1.5" /> Agregar criterio
                    </Button>
                  }
                />
              }
            />
          ) : (
            <div className="divide-y divide-border">
              {items.map((it) => {
                const meta = kindMeta[it.kind] ?? kindMeta.keyword;
                const Icon = meta.icon;
                return (
                  <div
                    key={it.id}
                    className={cn(
                      "flex items-center gap-3 p-4 hover:bg-muted/30",
                      !it.activo && "opacity-60",
                    )}
                  >
                    <div className="size-9 rounded-lg bg-muted grid place-items-center text-muted-foreground shrink-0">
                      <Icon className="size-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">{it.valor}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {meta.label} · seguido desde{" "}
                        {new Date(it.creadoEn).toLocaleDateString("es-CL")}
                        {!it.activo && " · en pausa"}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-mono tabular-nums text-primary">
                        {it.matches}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        vigentes · {it.matchesHistoricos} hist.
                      </div>
                    </div>
                    <Switch
                      checked={it.activo}
                      disabled={setActivo.isPending}
                      aria-label={`${it.activo ? "Pausar" : "Reactivar"} criterio ${it.valor}`}
                      onCheckedChange={(activo) => setActivo.mutate({ id: it.id, activo })}
                    />
                    <button
                      onClick={() => setPorEliminar(it)}
                      aria-label={`Eliminar criterio ${it.valor}`}
                      className="size-8 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition grid place-items-center"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card shadow-elegant p-5">
          <h3 className="font-display font-semibold mb-3 flex items-center gap-2">
            <span className={cn("size-2 rounded-full bg-primary animate-pulse")} /> Alertas IA en
            vivo
          </h3>
          {alertas.isLoading ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          ) : alertas.isError ? (
            <div className="text-xs text-muted-foreground">
              No pudimos cargar las alertas.{" "}
              <button className="text-primary underline" onClick={() => alertas.refetch()}>
                Reintentar
              </button>
            </div>
          ) : (alertas.data ?? []).length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Sin alertas por ahora. Cuando una licitación coincida con tus criterios y esté por
              cerrar, aparecerá aquí.
            </p>
          ) : (
            <div className="space-y-2">
              {(alertas.data ?? []).map((a) => (
                <SmartAlertItem key={a.id} alert={a} />
              ))}
            </div>
          )}
        </div>
      </div>

      <AlertDialog
        open={!!porEliminar}
        onOpenChange={(open) => !open && !remove.isPending && setPorEliminar(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar criterio</AlertDialogTitle>
            <AlertDialogDescription>
              Dejarás de recibir alertas de «{porEliminar?.valor}». Esta acción no se puede
              deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={remove.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (porEliminar) remove.mutate(porEliminar.id);
              }}
            >
              {remove.isPending && <Loader2 className="size-4 mr-1.5 animate-spin" />}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
